import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../AdminLayout';
import { supabase } from '../supabase';
import { Edit, Search, Trash2 } from 'lucide-react';
import { formatPhoneCanonical, handlePhoneInput } from '../../utils/phone';
import { NON_TEACHING_TYPES_FILTER } from '../../staff-app/programSessions';
import { compareProgramTypes, getProgramTypeMeta } from '../../types/program';
import {
  ALL_VIRTUAL_CLASSES,
  VIRTUAL_SUBJECTS,
  parseVirtualClassKey,
} from '../../staff-app/virtualClasses';
import { analytics } from '../../utils/analytics';
import { UnmappedGradeWarning } from '../components/UnmappedGradeWarning';


interface StaffUserRow {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  program_ids: string[] | null;
  /** Virtual classes (Chinese / Math). `null` means none — an explicit grant, unlike above. */
  virtual_class_keys: string[] | null;
  created_at: string;
}

interface ProgramOption {
  /** A `programs.id`, or a virtual class key such as `chinese:3`. */
  id: string;
  name: string;
  /** A `programs.type`, or `virtual:<subject>` for a virtual class. */
  type: string;
}

/** Section type for a virtual class option, keyed by subject so each gets its own header. */
function virtualSectionType(subjectId: string): string {
  return `virtual:${subjectId}`;
}

/**
 * The fourteen virtual classes as picker options, in catalog order.
 *
 * Appended after the real programs rather than merged into their sort, which is what keeps
 * them in grade order: sorting these by name would read G1, G2, G3, G4, G5, K, TK — no
 * order anyone says a grade list in.
 */
const VIRTUAL_OPTIONS: ProgramOption[] = ALL_VIRTUAL_CLASSES.map(vc => ({
  id: vc.key,
  // The full label, not just the grade, so a search for "Chinese" finds all seven.
  name: vc.label,
  type: virtualSectionType(vc.subject.id),
}));

/** Splits a picker selection back into the two columns it is stored in. */
export function splitAssignments(selected: readonly string[]): {
  programIds: string[];
  virtualClassKeys: string[];
} {
  const virtualClassKeys: string[] = [];
  const programIds: string[] = [];
  for (const id of selected) {
    if (parseVirtualClassKey(id)) virtualClassKeys.push(id);
    else programIds.push(id);
  }
  return { programIds, virtualClassKeys };
}

/**
 * What to store in `program_ids`.
 *
 * `null` means EVERY program — including ones created next year — so it is a genuinely
 * stronger grant than ticking today's programs one by one, and the admin has to say which
 * they mean. It used to be inferred from an empty selection, which quietly cost an
 * all-programs admin that grant the moment they ticked a single Chinese class.
 */
export function programIdsToStore(
  allPrograms: boolean,
  programIds: readonly string[],
): string[] | null {
  return allPrograms ? null : [...programIds];
}

/**
 * Program assignments for one teacher. Assignments pointing at a program the
 * Teacher Portal doesn't show — deactivated, or a non-teaching type such as
 * `registration` — are hidden: the portal filters every scoped query the same
 * way, so a stale id grants no access and only adds noise here.
 */
function TeacherProgramChips({
  programIds,
  virtualClassKeys,
  programNames,
}: {
  programIds: string[] | null;
  virtualClassKeys: string[] | null;
  programNames: Record<string, string>;
}) {
  // programNames only contains assignable programs, so this drops the rest.
  const activeIds = (programIds ?? []).filter(pid => programNames[pid]);
  const virtual = (virtualClassKeys ?? [])
    .map(key => parseVirtualClassKey(key))
    .filter((vc): vc is NonNullable<typeof vc> => vc !== null);

  const chips = (
    <div className="flex flex-wrap gap-1">
      {activeIds.map(pid => (
        <span key={pid} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">
          {programNames[pid]}
        </span>
      ))}
      {/* Deliberately a different colour from the program chips: "Chinese G3" is not a
          program, and an admin scanning this column should be able to see at a glance
          which assignments are derived rather than enrolled. */}
      {virtual.map(vc => (
        <span key={vc.key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-violet-50 text-violet-700">
          <span aria-hidden="true">{vc.subject.icon}</span>
          {vc.label}
        </span>
      ))}
    </div>
  );

  // `null` means every program; `[]` means none. They used to render identically, which
  // was harmless while nothing could produce `[]` — a teacher assigned only virtual
  // classes now can, and calling that "All programs" would be exactly backwards.
  if (programIds === null) {
    return (
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-xs text-gray-400 italic">All programs</span>
        {virtual.length > 0 && chips}
      </div>
    );
  }

  if (activeIds.length === 0 && virtual.length === 0) {
    return <span className="text-xs text-gray-400 italic">No active programs</span>;
  }

  return chips;
}

/**
 * Program assignment picker.
 *
 * Was a two-column checkbox grid with `truncate` on each name. That is unusable on a
 * phone: half a sheet's width cuts "Master Moves Chess Inter II (Fall 2026)" down to
 * "Master Moves Ch…", which is indistinguishable from the two sibling chess classes.
 * One full-width row per program, wrapping instead of truncating, is the fix — the
 * names are the only thing that tells the options apart.
 *
 * Rows are grouped under their program type and a search box appears once the list
 * outgrows a glance, so finding one class among a school year's worth stays a scan
 * rather than a scroll.
 */
function ProgramPicker({
  programs,
  selectedIds,
  allPrograms,
  onToggle,
  onToggleAllPrograms,
  onToggleSection,
  onClearAll,
}: {
  programs: ProgramOption[];
  selectedIds: string[];
  allPrograms: boolean;
  onToggle: (id: string) => void;
  onToggleAllPrograms: (next: boolean) => void;
  /** Tick or clear a whole section at once — the fourteen classes, seven at a time. */
  onToggleSection: (ids: string[], next: boolean) => void;
  onClearAll: () => void;
}) {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? programs.filter(p => p.name.toLowerCase().includes(needle))
    : programs;

  // `programs` arrives sorted by type then name, so a single pass yields the sections.
  const sections: { type: string; items: ProgramOption[] }[] = [];
  for (const p of visible) {
    const last = sections[sections.length - 1];
    if (last && last.type === p.type) last.items.push(p);
    else sections.push({ type: p.type, items: [p] });
  }

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <label className="block text-sm font-medium text-gray-700">
          Programs &amp; classes
        </label>
        {selectedIds.length > 0 && (
          <button
            type="button"
            onClick={onClearAll}
            className="flex-shrink-0 text-xs text-gray-500 hover:text-gray-700 hover:underline"
          >
            Clear {selectedIds.length}
          </button>
        )}
      </div>

      {/* An explicit grant, not an inference. Ticking "All programs" stores NULL, which
          covers programs created later too — strictly stronger than enumerating today's,
          and the reason a Select-all button would be the wrong shape for this half. It is
          also what lets an all-programs admin add Chinese to themselves without silently
          giving that grant up, which ticking a class used to do. */}
      <label className="flex items-center gap-2.5 px-3 py-2 mb-1.5 rounded-lg border border-gray-200 bg-white cursor-pointer hover:bg-gray-50">
        <input
          type="checkbox"
          checked={allPrograms}
          onChange={e => onToggleAllPrograms(e.target.checked)}
          className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700">
          All programs
          <span className="text-gray-400 text-xs"> — including any added later</span>
        </span>
      </label>

      {programs.length === 0 ? (
        <p className="text-sm text-gray-400 border border-gray-100 rounded-lg p-3 bg-gray-50">
          No active programs to assign.
        </p>
      ) : (
        <div className="border border-gray-100 rounded-lg bg-gray-50 overflow-hidden">
          {programs.length > 6 && (
            <div className="relative border-b border-gray-200 bg-white">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search programs…"
                className="w-full pl-9 pr-3 py-2 text-sm bg-transparent focus:outline-none"
              />
            </div>
          )}

          <div className="max-h-56 sm:max-h-64 overflow-y-auto p-1.5">
            {sections.length === 0 ? (
              <p className="text-sm text-gray-400 px-2 py-3">No programs match “{query.trim()}”.</p>
            ) : (
              sections.map(section => {
                const meta = getProgramTypeMeta(section.type);
                const subject = VIRTUAL_SUBJECTS.find(
                  s => section.type === virtualSectionType(s.id),
                );
                const sectionIds = section.items.map(i => i.id);
                const allTicked = sectionIds.every(id => selectedIds.includes(id));
                // Chinese and Math are unaffected by "All programs" — that flag is about
                // programs, and the two grants are stored separately. Dimming the program
                // rows is what makes that visible instead of leaving an admin wondering
                // whether their ticks still count.
                const supersededByAll = allPrograms && !subject;
                return (
                  <div key={section.type}>
                    <div className="flex items-baseline justify-between gap-2 px-2 pt-2 pb-1">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                        {subject
                          ? `${subject.icon} ${subject.label}`
                          : meta ? `${meta.icon} ${meta.label}` : section.type}
                      </p>
                      {/* Seven grades a subject is a lot of clicking to grant one teacher
                          the whole of Chinese, which is the common case. Safe to enumerate
                          here, unlike programs: the fourteen classes are a closed set that
                          never grows. */}
                      {section.items.length > 1 && !supersededByAll && (
                        <button
                          type="button"
                          onClick={() => onToggleSection(sectionIds, !allTicked)}
                          className="flex-shrink-0 text-[11px] text-gray-500 hover:text-gray-700 hover:underline"
                        >
                          {allTicked ? 'Clear' : 'Select all'}
                        </button>
                      )}
                    </div>
                    {section.items.map(p => (
                      <label
                        key={p.id}
                        className={`flex items-start gap-2.5 px-2 py-2 rounded-lg ${
                          supersededByAll
                            ? 'opacity-40 cursor-not-allowed'
                            : 'cursor-pointer hover:bg-white'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={supersededByAll || selectedIds.includes(p.id)}
                          disabled={supersededByAll}
                          onChange={() => onToggle(p.id)}
                          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 leading-snug">{p.name}</span>
                      </label>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

type ModalMode = 'add' | 'edit';

interface ModalState {
  mode: ModalMode;
  id?: string;
  email: string;
  name: string;
  phone: string;
  /** Ticked ids — real programs and virtual class keys mixed; split on save. */
  program_ids: string[];
  /** Stores `program_ids = null`: every program, including ones added later. */
  all_programs: boolean;
}

function TeacherModal({
  state,
  programs,
  saving,
  error,
  onChange,
  onToggleProgram,
  onToggleAllPrograms,
  onToggleSection,
  onClearPrograms,
  onSave,
  onClose,
}: {
  state: ModalState;
  programs: ProgramOption[];
  saving: boolean;
  error: string | null;
  onChange: (field: 'email' | 'name' | 'phone', value: string) => void;
  onToggleProgram: (id: string) => void;
  onToggleAllPrograms: (next: boolean) => void;
  onToggleSection: (ids: string[], next: boolean) => void;
  onClearPrograms: () => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const isEdit = state.mode === 'edit';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex flex-col justify-end sm:items-center sm:justify-center z-[60]"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Caps at the viewport so the sheet scrolls internally instead of pushing
          the Save button off a short phone screen. */}
      <div className="bg-white rounded-t-2xl sm:rounded-xl p-6 w-full sm:max-w-lg sm:mx-4 space-y-4 max-h-[90vh] overflow-y-auto">
        {/* Mobile drag handle */}
        <div className="sm:hidden flex justify-center -mt-2 mb-2">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {isEdit ? 'Edit Teacher' : 'Add Teacher'}
        </h3>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email {!isEdit && <span className="text-red-500">*</span>}
            </label>
            {isEdit ? (
              <p className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">{state.email}</p>
            ) : (
              <input
                type="email"
                value={state.email}
                onChange={e => onChange('email', e.target.value)}
                placeholder="teacher@example.com"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
              <input
                type="text"
                value={state.name}
                onChange={e => onChange('name', e.target.value)}
                placeholder="Ms. Chen (optional)"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={state.phone}
                onChange={e => onChange('phone', handlePhoneInput(e.target.value))}
                placeholder="(555) 555-5555"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <ProgramPicker
            programs={programs}
            selectedIds={state.program_ids}
            allPrograms={state.all_programs}
            onToggle={onToggleProgram}
            onToggleAllPrograms={onToggleAllPrograms}
            onToggleSection={onToggleSection}
            onClearAll={onClearPrograms}
          />
        </div>

        {!isEdit && (
          <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
            After adding, ask the teacher to sign in at{' '}
            <span className="font-mono">portal.bhcskids.com</span> using their Google account
            at the email address above. Their Teacher Portal access activates on first sign-in.
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={onSave}
            disabled={saving}
            className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Teacher'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 sm:flex-none px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminTeachers() {
  const [teachers, setTeachers] = useState<StaffUserRow[]>([]);
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [programNames, setProgramNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  useEffect(() => { void loadData(); }, []);

  async function loadData() {
    setLoading(true);
    const [teachersRes, assignableProgramsRes] = await Promise.all([
      supabase.from('staff_users').select('id, email, name, phone, program_ids, virtual_class_keys, created_at').order('email'),
      // Same filter the Teacher Portal applies to every scoped query: active, and not a
      // non-teaching type. Registration products (New Student Registration, Waitlist) carry
      // enrollments but have no class to teach, so assigning one grants a teacher nothing —
      // offering them here only made the real classes harder to find.
      supabase
        .from('programs')
        .select('id, name, type')
        .eq('active', true)
        .not('type', 'in', NON_TEACHING_TYPES_FILTER)
        .order('name'),
    ]);
    if (teachersRes.data) setTeachers(teachersRes.data as StaffUserRow[]);
    if (assignableProgramsRes.data) {
      // Name order from the server, then a stable regroup by type for the picker sections.
      const assignable = [...(assignableProgramsRes.data as ProgramOption[])].sort(
        (a, b) => compareProgramTypes(a.type, b.type) || a.name.localeCompare(b.name)
      );
      // Virtual classes are appended, never merged into the sort above: they have no
      // programs row to sort among, and sorting them by name would read G1…G5, K, TK.
      setPrograms([...assignable, ...VIRTUAL_OPTIONS]);
      setProgramNames(Object.fromEntries(assignable.map(p => [p.id, p.name])));
    }
    setLoading(false);
  }

  function openAdd() {
    // A new teacher starts on "all programs", which is what a blank picker has always
    // meant here — the toggle makes it visible rather than changing it.
    setModal({ mode: 'add', email: '', name: '', phone: '', program_ids: [], all_programs: true });
    setModalError(null);
  }

  function openEdit(t: StaffUserRow) {
    // Drop ids the picker can no longer show (deactivated or non-teaching programs) so what
    // the admin sees is what a save writes back — otherwise an unchecked-looking assignment
    // would silently survive the edit. Grants nothing either way; the portal filters the same.
    const assignable = (t.program_ids ?? []).filter(id => programNames[id]);
    // Virtual keys share the picker's one selection list; `splitAssignments` separates them
    // again on save. A key that no longer parses is dropped for the same reason as above.
    const virtual = (t.virtual_class_keys ?? []).filter(key => parseVirtualClassKey(key));
    setModal({
      mode: 'edit', id: t.id, email: t.email, name: t.name ?? '', phone: t.phone ?? '',
      program_ids: [...assignable, ...virtual],
      all_programs: t.program_ids === null,
    });
    setModalError(null);
    setConfirmRemoveId(null);
  }

  function closeModal() {
    setModal(null);
    setModalError(null);
  }

  function handleModalChange(field: 'email' | 'name' | 'phone', value: string) {
    setModal(m => m ? { ...m, [field]: value } : null);
  }

  function handleToggleProgram(id: string) {
    setModal(m => {
      if (!m) return null;
      return {
        ...m,
        program_ids: m.program_ids.includes(id)
          ? m.program_ids.filter(p => p !== id)
          : [...m.program_ids, id],
      };
    });
  }

  function handleToggleAllPrograms(next: boolean) {
    // Ticking "All programs" leaves any individually-ticked programs in place rather than
    // wiping them: untick it again and the admin gets their selection back instead of a
    // blank list. They are simply not written while the flag is on.
    setModal(m => m ? { ...m, all_programs: next } : null);
  }

  function handleToggleSection(ids: string[], next: boolean) {
    setModal(m => {
      if (!m) return null;
      const remaining = m.program_ids.filter(id => !ids.includes(id));
      return { ...m, program_ids: next ? [...remaining, ...ids] : remaining };
    });
  }

  function handleClearPrograms() {
    setModal(m => m ? { ...m, program_ids: [], all_programs: false } : null);
  }

  async function handleSave() {
    if (!modal) return;
    if (modal.mode === 'add' && !modal.email.trim()) { setModalError('Email is required'); return; }
    setSaving(true);
    setModalError(null);

    // One picker, two columns. The split has to survive the round trip in both directions:
    // clearing every program must not clear the Chinese classes, and vice versa.
    const { programIds, virtualClassKeys } = splitAssignments(modal.program_ids);

    const payload = {
      name: modal.name.trim() || null,
      phone: modal.phone.trim() ? formatPhoneCanonical(modal.phone.trim()) : null,
      program_ids: programIdsToStore(modal.all_programs, programIds),
      virtual_class_keys: virtualClassKeys.length > 0 ? virtualClassKeys : null,
    };

    const { error: err } = modal.mode === 'add'
      ? await supabase.from('staff_users').insert({ ...payload, email: modal.email.trim().toLowerCase() })
      : await supabase.from('staff_users').update(payload).eq('id', modal.id!);

    if (err) { setModalError(err.message); setSaving(false); return; }

    // A staff_users row is an access grant: it decides which programs' rosters and children
    // a teacher can see.
    analytics.trackAdminAction(modal.mode === 'add' ? 'admin_teacher_create' : 'admin_teacher_update', {
      entityType: 'staff_user',
      entityId: modal.mode === 'add' ? undefined : modal.id!,
      metadata: {
        teacher_email: modal.email.trim().toLowerCase(),
        teacher_name: payload.name,
        // null program_ids means every program — worth being able to see in the log.
        program_count: payload.program_ids ? payload.program_ids.length : null,
        scoped_to_all_programs: payload.program_ids === null,
        virtual_class_count: virtualClassKeys.length,
        virtual_classes: virtualClassKeys.length > 0 ? virtualClassKeys.join(',') : null,
      },
    });
    closeModal();
    await loadData();
    setSaving(false);
  }

  async function handleRemove(id: string) {
    const removed = teachers.find(t => t.id === id);
    const { error: err } = await supabase.from('staff_users').delete().eq('id', id);
    if (err) { setPageError(err.message); return; }

    analytics.trackAdminAction('admin_teacher_delete', {
      entityType: 'staff_user',
      entityId: id,
      metadata: { teacher_email: removed?.email ?? null, teacher_name: removed?.name ?? null },
    });
    setConfirmRemoveId(null);
    setSelectedTeacherId(null);
    await loadData();
  }

  return (
    <AdminLayout>
      {/* Placed here rather than on the students page: this is the screen where Chinese and
          Math get assigned, so it is where "a child is missing from a roster" is actionable. */}
      <UnmappedGradeWarning />
      <div className="bg-white shadow overflow-hidden sm:rounded-lg flex flex-col min-h-0 flex-1">
        <div className="px-4 py-5 sm:px-6 border-b flex-shrink-0 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Teachers</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              Teachers can view programs, students, and enrollments — no financial data.
              {/* The picker itself now carries this rule, so a phone doesn't need it twice. */}
              <span className="hidden sm:inline">
                {' '}Leave blank to grant all programs — Chinese and Math classes are never
                granted by default and must be ticked.
              </span>
            </p>
          </div>
          <button
            onClick={openAdd}
            className="flex-shrink-0 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Teacher
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {pageError && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-4">{pageError}</p>
          )}

          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-xl" />)}
            </div>
          ) : teachers.length === 0 ? (
            <p className="text-sm text-gray-400">No teachers added yet.</p>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              {/* Desktop table */}
              <table className="min-w-full text-sm hidden lg:table">
                <thead className="bg-gray-50">
                  <tr>
                    {['Name / Email', 'Phone', 'Programs', 'Added'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {teachers.map(t => (
                    <React.Fragment key={t.id}>
                      <tr
                        onClick={() => { setSelectedTeacherId(selectedTeacherId === t.id ? null : t.id); setConfirmRemoveId(null); }}
                        className={`cursor-pointer transition-colors ${selectedTeacherId === t.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-4 py-3">
                          {t.name && <p className="font-medium text-gray-900">{t.name}</p>}
                          <p className={t.name ? 'text-xs text-gray-400' : 'text-gray-700'}>{t.email}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {t.phone
                            ? <a href={`tel:${t.phone}`} onClick={e => e.stopPropagation()} className="hover:underline">{t.phone}</a>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <TeacherProgramChips programIds={t.program_ids} virtualClassKeys={t.virtual_class_keys} programNames={programNames} />
                        </td>
                        <td className="px-4 py-3 text-gray-400 whitespace-nowrap text-xs">
                          {new Date(t.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                      {selectedTeacherId === t.id && (
                        <tr key={`${t.id}-actions`} className="bg-blue-50">
                          <td colSpan={4} className="px-4 py-2 border-t border-blue-100">
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Edit
                              </button>
                              {confirmRemoveId === t.id ? (
                                <>
                                  <span className="text-xs text-gray-500 self-center">Remove?</span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); void handleRemove(t.id); }}
                                    className="text-xs text-red-600 font-medium hover:underline self-center"
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setConfirmRemoveId(null); }}
                                    className="text-xs text-gray-500 hover:underline self-center"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setConfirmRemoveId(t.id); }}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Remove
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              {/* Mobile card list */}
              <div className="lg:hidden divide-y divide-gray-100">
                {teachers.map(t => (
                  <React.Fragment key={t.id}>
                    <div
                      onClick={() => { setSelectedTeacherId(selectedTeacherId === t.id ? null : t.id); setConfirmRemoveId(null); }}
                      className={`p-4 cursor-pointer transition-colors ${selectedTeacherId === t.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    >
                      {/* Name + email */}
                      {t.name && <p className="font-semibold text-gray-900">{t.name}</p>}
                      <p className={t.name ? 'text-xs text-gray-400' : 'text-sm font-medium text-gray-900'}>{t.email}</p>

                      {/* Phone */}
                      {t.phone && (
                        <a
                          href={`tel:${t.phone}`}
                          onClick={e => e.stopPropagation()}
                          className="text-sm text-gray-500 hover:underline mt-1 block"
                        >
                          {t.phone}
                        </a>
                      )}

                      {/* Programs */}
                      <div className="mt-2">
                        <TeacherProgramChips programIds={t.program_ids} virtualClassKeys={t.virtual_class_keys} programNames={programNames} />
                      </div>
                    </div>

                    {/* Action row */}
                    {selectedTeacherId === t.id && (
                      <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 flex flex-wrap gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </button>
                        {confirmRemoveId === t.id ? (
                          <>
                            <span className="text-xs text-gray-500 self-center">Remove?</span>
                            <button onClick={(e) => { e.stopPropagation(); void handleRemove(t.id); }} className="text-xs text-red-600 font-medium hover:underline self-center">Yes</button>
                            <button onClick={(e) => { e.stopPropagation(); setConfirmRemoveId(null); }} className="text-xs text-gray-500 hover:underline self-center">Cancel</button>
                          </>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmRemoveId(t.id); }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <TeacherModal
          state={modal}
          programs={programs}
          saving={saving}
          error={modalError}
          onChange={handleModalChange}
          onToggleProgram={handleToggleProgram}
          onToggleAllPrograms={handleToggleAllPrograms}
          onToggleSection={handleToggleSection}
          onClearPrograms={handleClearPrograms}
          onSave={() => void handleSave()}
          onClose={closeModal}
        />
      )}
    </AdminLayout>
  );
}
